import React, { useState, useEffect } from 'react';
import { LuLogIn } from 'react-icons/lu';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Topbar from './components/Topbar.js';
import Home from './pages/Home.js';
import AdminPage from './pages/AdminPage.jsx';
import { API_BASE_URL, CONNECT_UI_URL, CONNECT_CLIENT_ID } from './config.js';
import { removeCookie } from './utils/cookies.js';
import {
  isEmbedded,
  consumeConnectError,
  endAllSessions,
  startSessionWatch,
  isSessionLoss,
} from '@globegroupe-data/auth-kit/browser';
import WeeklyReportHome from './pages/WeeklyReportHome.jsx';
import ClientViewer from './components/weekly/ClientViewer.jsx';
import { WeeklyReportRoute } from './components/weekly/WeeklyReport.jsx';
import PublicReport from './components/weekly/PublicReport.jsx';
import GlobeMark from './components/ui/GlobeMark.jsx';
import Spinner, { SpinnerRing } from './components/ui/Spinner.jsx';
import { ErrorMessage } from './components/ui/ErrorMessage.jsx';
import ErrorBoundary from './components/ui/ErrorBoundary.jsx';
import EmptyState from './components/ui/EmptyState.jsx';
import Button from './components/ui/Button.jsx';
import useAnalytics from './hooks/useAnalytics.jsx';

// Wrapper component to handle report context
const AppContent = ({ isAuthenticated, currentUser, logout, fetchWithAuth }) => {
  const location = useLocation();
  const isPublicShareRoute = location.pathname.startsWith('/share/');

  useAnalytics({ currentUser: isAuthenticated ? currentUser : null });

  return (
    <div className="App flex h-full flex-col bg-[#fafafa] text-gray-900 transition-colors duration-200 dark:bg-night-950 dark:text-gray-100">
      <Topbar
        currentUser={isAuthenticated ? currentUser : null}
        logout={isAuthenticated ? logout : null}
      />

      <main className="min-h-0 flex-1 overflow-y-auto">
        {(isAuthenticated || isPublicShareRoute) ? (
          <ErrorBoundary
            resetKeys={[location.pathname]}
            fallback={<ErrorMessage title="Une erreur est survenue" message="Cette page n'a pas pu s'afficher. Rechargez-la ou revenez à l'accueil." />}
          >
            <Routes>
              {isAuthenticated && (
                <>
                  <Route path="/" element={
                    <Home
                      currentUser={currentUser}
                      fetchWithAuth={fetchWithAuth}
                    />
                  } />
                  <Route
                    path="/admin/*"
                    element={
                      <AdminPage
                        fetchWithAuth={fetchWithAuth}
                        currentUser={currentUser}
                      />
                    }
                  />
                  {/* One layout for the list and the reports: the sidebar stays mounted between them. */}
                  <Route path="/w/:slug" element={
                    <ClientViewer
                      fetchWithAuth={fetchWithAuth}
                      currentUser={currentUser}
                    />
                  }>
                    <Route index element={
                      <WeeklyReportHome
                        fetchWithAuth={fetchWithAuth}
                        currentUser={currentUser}
                      />
                    } />
                    <Route path=":id" element={
                      <WeeklyReportRoute
                        fetchWithAuth={fetchWithAuth}
                        currentUser={currentUser}
                      />
                    } />
                  </Route>
                  <Route path="*" element={
                    <ErrorMessage title="Page introuvable" message="Cette adresse ne correspond à aucune page de Globe Weekly." />
                  } />
                </>
              )}

              {/* Public route: no authentication */}
              <Route path="/share/:id/:token" element={
                <PublicReport />
              } />
            </Routes>
          </ErrorBoundary>
        ) : (
          <Spinner message="Redirection vers Connect…" />
        )}
      </main>
    </div>
  );
};

function App() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const isPublicShareRoute = window.location.pathname.startsWith('/share/');

  // In an iframe, ask Connect for a SILENT authorization (silent=1, no form in
  // the frame); on failure the callback bounces back with ?connect_error.
  const redirectToConnect = ({ silent = isEmbedded() } = {}) => {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.assign(`${API_BASE_URL}/auth/login?returnTo=${returnTo}${silent ? '&silent=1' : ''}`);
  };

  const clearAuthState = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  // Embed contract (Gallery PR #81): inside a frame, ONE silent re-login attempt,
  // never a takeover screen — the parent shell judges logout. Top-level: interactive.
  const onSessionExpired = () => {
    if (isEmbedded()) {
      redirectToConnect({ silent: true });
      return;
    }
    redirectToConnect({ silent: false });
  };

  const fetchWithAuth = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: options.credentials || 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // isSessionLoss et pas le statut : seuls les 401 portant le challenge
    // `WWW-Authenticate: Session` viennent de la couche session. Les 401 applicatifs
    // (hasReportAccess, routes admin) ne doivent déconnecter personne.
    if (isSessionLoss(response)) {
      if (!options.noLogOut) {
        clearAuthState();
        onSessionExpired();
      }

      const error = new Error('Authentication failed');
      error.response = response;
      // Le verdict voyage avec l'erreur : les appelants n'ont jamais à re-deviner
      // depuis le statut brut (c'est le branchement que cette migration élimine).
      error.sessionLoss = true;
      throw error;
    }

    // Les refus applicatifs (REPORT_ACCESS_DENIED, admin) rendent la réponse telle
    // quelle : pas de challenge dessus, donc pas de déconnexion — le composant l'affiche.
    return response;
  };

  const logout = async () => {
    // Déconnexion = déconnexion : la session SSO tombe aussi, sinon les autres
    // apps de la famille resteraient connectées.
    await endAllSessions({
      apiBaseUrl: API_BASE_URL,
      connectUrl: CONNECT_UI_URL,
      clientId: CONNECT_CLIENT_ID,
    });

    clearAuthState();

    // Jamais de formulaire de login dans un cadre : l'écran de reconnexion rend
    // la main à l'app parente.
    if (isEmbedded()) {
      setSessionExpired(true);
      return;
    }

    redirectToConnect({ silent: false });
  };

  // Focus-only watch (auth-kit 0.10.0). None at all when embedded: the parent
  // shell watches the session; the frame only heals itself through its 401 handler.
  useEffect(() => {
    if (!isAuthenticated || isEmbedded()) return undefined;
    return startSessionWatch({
      probeUrl: `${API_BASE_URL}/api/me`,
      onSessionLost: onSessionExpired,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Check authentication status and fetch user data on mount
  useEffect(() => {
    // Purge du jeton de l'ancienne auth Basic : base64(login:motdepasse), posé par
    // CETTE app sur tout .globe-groupe.com — le cookie qui sabotait la reconnexion
    // silencieuse de Gallery (identité croisée, Gallery PR #80). Suppression à
    // chaque montage tant que des cookies résiduels existent.
    localStorage.removeItem('authToken');
    removeCookie('authToken');

    // ?connect_error = échec du login silencieux (pas de session SSO) :
    // placeholder dans un iframe, login interactif au top-level.
    if (consumeConnectError()) {
      if (isEmbedded()) {
        setSessionExpired(true);
        setIsAuthenticating(false);
        return;
      }
      redirectToConnect({ silent: false });
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/me`, { noLogOut: true });

        if (!response.ok) {
          const error = new Error('Authentication failed');
          error.response = response;
          throw error;
        }

        const userData = await response.json();
        setCurrentUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        clearAuthState();

        // Une route publique reste consultable sans session : pas de rebond.
        if (isPublicShareRoute) {
          setIsAuthenticating(false);
          return;
        }

        // Seule une perte de session justifie le voyage vers Connect ; tout le reste
        // (403 compte inconnu/inactif, 5xx) bouclerait — on affiche l'erreur. Le
        // verdict vient de isSessionLoss via fetchWithAuth, jamais du statut brut.
        const status = error?.response?.status;
        if (error?.sessionLoss || !status) {
          redirectToConnect();
        } else {
          setAuthError(`Authentification impossible (HTTP ${status}).`);
        }
      }

      setIsAuthenticating(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session SSO morte dans un iframe : recharger LE CADRE relance une tentative
  // silencieuse (consumeConnectError est mémoïsé par chargement de page).
  // Jamais window.top : éjecter l'utilisateur de son shell parent n'appartient
  // pas à un cadre.
  if (sessionExpired) {
    return (
      <div className="flex h-full items-center justify-center bg-[#fafafa] dark:bg-night-950">
        <EmptyState
          bordered
          icon={LuLogIn}
          title="Session expirée"
          action={<Button onClick={() => window.location.reload()}>Réessayer</Button>}
        />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex h-full items-center justify-center bg-[#fafafa] dark:bg-night-950">
        <ErrorMessage title="Authentification impossible" message={authError} showHomeLink={false} />
      </div>
    );
  }

  if (isAuthenticating) {
    return (
      <div className="flex h-full items-center justify-center bg-[#fafafa] dark:bg-night-950">
        <div className="flex flex-col items-center gap-4">
          <GlobeMark className="h-10 w-10 text-[#00529C] dark:text-white" />
          <SpinnerRing size="md" label="Chargement de Globe Weekly…" />
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        logout={logout}
        fetchWithAuth={fetchWithAuth}
      />
    </Router>
  );
}

export default App;
